import { NEW_LOOK_RADIUS } from "common/NewLook/tokens.ts";
import { MOBILE_DASHBOARD_BALANCE_ID } from "testIds";
import styled from "styled-components";
import Text from "common/Typography/Text";
import { typography } from "@shared/common/Themes/typography";
import { useDashboardBalance } from "@shared/features/dashboard/hooks";
import { LanguageNameSpaces, TimeRange } from "@shared/types";
import { ICON_TYPES } from "@shared/common/constants";
import React, { CSSProperties, useMemo } from "react";
import { hexToRGB } from "@shared/utils/misc";
import ChangeTag from "common/Tags/ChangeTag";
import { toFixed } from "@shared/utils/numbers";
import ExpandingChart, {
  EXPANDED_CHART_HEIGHT,
  TIME_RANGE_BUTTONS_HEIGHT,
} from "features/dashboard/MobileDashboard/ExpandingChart.tsx";
import TextWithIcon from "common/Text/TextWithIcon";
import { DISPLAY_CURRENCIES } from "common/constants";
import { getUsdValueWithPrecision } from "features/dashboard/common/utils";
import StatsAndChartCard from "common/Boxes/StatsAndChartCard.tsx";
import Flex from "common/Boxes/Flex.tsx";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";

interface BalanceProps {
  selectedTimeRange: TimeRange;
  setSelectedTimeRange: (timeRange: TimeRange) => void;
  isFetching: boolean;
  isSuccess: boolean;
}

const Balance = ({
  selectedTimeRange,
  setSelectedTimeRange,
  isFetching,
  isSuccess,
}: BalanceProps) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.DASHBOARD);
  const {
    theme,
    displayCurrency,
    graphData,
    balanceValues,
    expanded,
    setExpanded,
    balancePrecision,
  } = useDashboardBalance({
    selectedTimeRange,
  });

  const getTextWithCurrency = (
    value: number,
    size: number | string,
    iconSize: number | string,
    style?: CSSProperties
  ) => {
    return (
      <TextWithIcon
        text={value}
        iconType={ICON_TYPES.DISPLAY_CURRENCY}
        style={{ alignItems: "baseline", ...style }}
        textStyle={{
          fontSize: size,
          color: theme.isNewLook ? theme.colors.titleColor : theme.colors.white,
        }}
        iconStyle={{
          fontSize: iconSize,
          color: theme.isNewLook
            ? theme.colors.secondaryText
            : hexToRGB(theme.colors.white, 0.5),
          marginRight: 2,
        }}
      />
    );
  };

  const getFontSize = (value: number) => {
    const strValueLength = value.toString().replace(/\./g, "").length;
    if (strValueLength > 10) return 16;
    if (strValueLength > 8) return 18;
    if (strValueLength > 6) return 20;
    else return 24;
  };

  const balanceWithPrecision = useMemo(
    () =>
      displayCurrency === DISPLAY_CURRENCIES.USD
        ? getUsdValueWithPrecision(balanceValues.balance)
        : balanceValues.balance,
    [balanceValues.balance, displayCurrency]
  );

  const balance = (
    <div data-testid={MOBILE_DASHBOARD_BALANCE_ID}>
      <StyledText $size={typography.fontSizes.S}>
        {t("balance.title")}
      </StyledText>
      {getTextWithCurrency(
        balanceWithPrecision,
        getFontSize(balanceWithPrecision),
        theme.typography.fontSizes.L
      )}
    </div>
  );

  const change = (
    <ChangeWrapper $expanded={expanded}>
      {getTextWithCurrency(
        getUsdValueWithPrecision(Math.abs(balanceValues.change) || 0),
        typography.fontSizes.S,
        theme.typography.fontSizes.S,
        { alignItems: "center" }
      )}
      {theme.isNewLook ? (
        <NewLookChange $negative={balanceValues.percentChange < 0}>
          {balanceValues.percentChange > 0 && "+"}
          {balanceValues.percentChange < 0 && "−"}
          {toFixed(Math.abs(balanceValues.percentChange * 100), 2)}%
        </NewLookChange>
      ) : (
        <ChangeTag
          value={toFixed(balanceValues.percentChange * 100, 2)}
          absoluteValue={true}
          suffix="%"
          noOpacityColor={true}
          style={{ fontSize: 11 }}
          arrowStyle={{ fontSize: 7 }}
          textStyle={{ padding: "2px 4px", color: theme.colors.layoutBgColor }}
        />
      )}
    </ChangeWrapper>
  );

  const chart = (
    <ExpandingChart
      graphData={graphData}
      selectedTimeRange={selectedTimeRange}
      setSelectedTimeRange={setSelectedTimeRange}
      precision={balancePrecision}
      loading={isFetching}
      isSuccess={isSuccess}
      expanded={expanded}
      hideXAxis={true}
      axisOverlapData={true}
      expandedChartHeight={EXPANDED_CHART_HEIGHT}
    />
  );

  const animateStatisticStyle = useMemo(() => {
    return {
      justifyContent: expanded ? "flex-end" : "flex-start",
    };
  }, [expanded]);

  return (
    <StatsAndChartCard
      topStatistic={balance}
      bottomStatistic={change}
      chart={chart}
      expanded={expanded}
      setExpanded={setExpanded}
      style={STYLE}
      animateStatisticStyle={animateStatisticStyle}
      collapsedHeight={100}
      expandedHeight={EXPANDED_CHART_HEIGHT + TIME_RANGE_BUTTONS_HEIGHT + 16}
    />
  );
};

export default Balance;

const STYLE = { marginBottom: 20 };

const StyledText = styled(Text)`
  color: ${(p) =>
    p.theme.isNewLook ? p.theme.colors.secondaryText : p.theme.colors.white};
`;
const NewLookChange = styled.span<{ $negative: boolean }>`
  margin-left: 8px;
  padding: 1px 8px;
  border-radius: ${NEW_LOOK_RADIUS.chip}px;
  background: ${(p) =>
    hexToRGB(p.$negative ? p.theme.colors.red : p.theme.colors.green, 0.08)};
  color: ${(p) => (p.$negative ? p.theme.colors.red : p.theme.colors.green)};
  font-size: 11.5px;
  line-height: 18px;
  font-weight: ${(p) => p.theme.typography.fontWeights.MEDIUM};
  font-variant-numeric: tabular-nums;
`;
const ChangeWrapper = styled(Flex)<{ $expanded?: boolean }>`
  justify-content: ${(p) => (p.$expanded ? "flex-end" : "flex-start")};
  margin-bottom: ${(p) => (p.$expanded ? 8 : 0)}px;
`;
